import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode } from '@aws-amplify/auth';
import { configureAuth } from '../../auth/authConfig';
import { withSessionLock } from '../../auth/sessionQueue';
import { getSafeReaderReturnTo } from '../../auth/readerReturn';
import { authorLoginReturnPath } from '../../services/groupInvitations';
import PublicHeader from './PublicHeader';

const inputClass = 'w-full border border-bookish-purple/15 rounded-lg px-3 py-2 min-h-[44px] bg-white focus:ring-2 focus:ring-bookish-purple/30';
const buttonClass = 'w-full inline-flex justify-center items-center bg-bookish-royal text-white rounded-lg py-3 min-h-[44px] font-semibold hover:bg-bookish-purpleAccent disabled:opacity-60';
function friendlyError(error) {
  const messages = {
    CodeMismatchException: 'That code doesn’t match. Use the latest signup verification code from your email.',
    ExpiredCodeException: 'That code has expired. Select “Resend code” for a new one.',
    LimitExceededException: 'Too many attempts. Please wait a little before trying again.',
    TooManyRequestsException: 'Too many attempts. Please wait a little before trying again.',
    UserNotFoundException: 'Check your email and account type, or return to signup to create your account.',
    NotAuthorizedException: 'We couldn’t verify this account. If you already verified your email, try logging in. Otherwise, check your details or contact Lady Liza below.',
    InvalidParameterException: 'Check your email and account type. If you already verified your email, try logging in.',
  };
  return messages[error?.name || error?.code] || 'We couldn’t complete that step. Please try again, or contact Lady Liza below for help.';
}

function VerificationForm({ search }) {
  const [initial] = useState(() => {
    const params = new URLSearchParams(search);
    const requested = params.get('type');
    return { type: ['author', 'reader'].includes(requested) ? requested : '', email: (params.get('email') || '').trim(), code: (params.get('code') || '').trim(), returnTo: params.get('returnTo') || '' };
  });
  const [type, setType] = useState(initial.type);
  const [email, setEmail] = useState(initial.email);
  const [code, setCode] = useState(initial.code);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [complete, setComplete] = useState(false);
  const lock = useRef(false);
  const started = useRef(false);
  const heading = useRef(null);
  const form = useRef(null);
  const audience = type === 'author' ? 'Author' : type === 'reader' ? 'Reader' : '';
  const returnTo = type === 'author' ? authorLoginReturnPath('?' + new URLSearchParams({ returnTo: initial.returnTo })) : getSafeReaderReturnTo(initial.returnTo);
  const suffix = returnTo ? '?' + new URLSearchParams({ returnTo }) : '';
  const loginPath = `/login${type}${suffix}`;

  useEffect(() => { heading.current?.focus(); }, [complete]);

  async function run(label, operation) {
    if (lock.current) return;
    lock.current = true; setBusy(label); setError(''); setMessage('');
    try { await operation(); }
    catch (err) { setError(friendlyError(err)); }
    finally { lock.current = false; setBusy(''); }
  }
  function verify(accountType, address, confirmationCode) {
    return run('Verifying your email…', async () => {
      const result = await withSessionLock(() => {
        configureAuth(accountType);
        return confirmSignUp({ username: address.trim(), confirmationCode: confirmationCode.trim() });
      });
      if (result?.isSignUpComplete || ['DONE', 'COMPLETE_AUTO_SIGN_IN'].includes(result?.nextStep?.signUpStep)) {
        setCode(''); setComplete(true);
      } else {
        setError('Your account still needs another step. Return to signup to continue, or contact Lady Liza below.');
      }
    });
  }
  useEffect(() => {
    // Old links omit the account type. Ask the person instead of guessing a pool.
    // The ref also prevents a second request during StrictMode effect replay.
    if (started.current) return;
    started.current = true;
    if (initial.type && initial.email && initial.code && form.current?.checkValidity()) {
      verify(initial.type, initial.email, initial.code);
    }
    // Only the original link should trigger automatic verification.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submit(event) {
    event.preventDefault();
    if (lock.current || !event.currentTarget.reportValidity()) return;
    if (!code.trim()) { setError('Enter the signup verification code from your email.'); return; }
    verify(type, email, code);
  }
  function resend() {
    if (lock.current) return;
    if (!type) { setError('Choose your Author or Reader account type first.'); return; }
    if (!form.current.elements.email.reportValidity()) return;
    run('Sending a new code…', async () => {
      await withSessionLock(() => { configureAuth(type); return resendSignUpCode({ username: email.trim() }); });
      setCode(''); setMessage('A new signup verification code is on its way. Check your inbox and spam folder, and use the most recent code.');
    });
  }
  function edit(setter, value) { setter(value); setError(''); setMessage(''); }

  return <div className="min-h-screen bg-bookish-cream text-bookish-royal"><PublicHeader audience={type || 'reader'} />
    <main className="account-page flex justify-center"><div className="bg-white p-6 sm:p-8 rounded-2xl shadow-[0_12px_40px_rgba(63,37,107,0.10)] w-full max-w-md border border-bookish-purple/8">
      <p className="text-sm uppercase tracking-widest mb-3">{audience ? `${audience} account` : 'Welcome to the Circle'}</p>
      <h1 ref={heading} tabIndex={-1} className="font-serif text-3xl font-bold mb-4">{complete ? 'Your email is verified' : 'Your next chapter starts here'}</h1>
      {complete ? <>
        <p role="status" className="mb-6">Welcome to the Circle! Your {audience} email is verified. Log in to continue.</p>
        <Link className={buttonClass} to={loginPath}>Log in to your {audience} account</Link>
      </> : <>
        <p className="mb-5">Verify your email to continue your story with the Bookish Circle.</p>
        {!initial.type && <p className="mb-4 text-sm">Choose the account type you signed up for so we can verify the right account.</p>}
        <form ref={form} aria-label="Email verification" aria-busy={Boolean(busy)} onSubmit={submit}>
          <fieldset disabled={Boolean(busy)} className="space-y-4">
            <div><label className="account-field" htmlFor="verify-type">Account type</label><select id="verify-type" required className={inputClass} value={type} onChange={e => edit(setType, e.target.value)}><option value="">Choose your account type</option><option value="author">Author</option><option value="reader">Reader</option></select></div>
            <div><label className="account-field" htmlFor="verify-email">Email</label><input id="verify-email" name="email" type="email" autoComplete="email" required className={inputClass} value={email} onChange={e => edit(setEmail, e.target.value.trim())} /></div>
            <div><label className="account-field" htmlFor="verify-code">Signup verification code</label><input id="verify-code" required autoComplete="one-time-code" inputMode="numeric" aria-describedby="verify-help" className={inputClass} value={code} onChange={e => edit(setCode, e.target.value)} /></div>
            <p id="verify-help" className="text-sm">Use the latest signup verification code from your email, not a password-reset code. Check your spam folder if it hasn’t arrived. If your link is incomplete, enter your details here or request a new code.</p>
            <p role="alert" className="text-sm">{error}</p><p role="status" className="text-sm">{busy || message}</p>
            <button type="submit" className={buttonClass}>{busy || 'Verify email'}</button>
            <button type="button" className="underline min-h-[44px]" onClick={resend}>Resend code</button>
          </fieldset>
        </form>
        {type && <div className="mt-5"><Link className="block py-3 underline" to={loginPath}>Back to {audience} Login</Link><Link className="block py-3 underline" to={`/signup${type}${suffix}`} state={{ resumeVerification: true, email }}>Continue {audience} signup</Link></div>}
      </>}
      <p className="mt-5 text-sm">Need a hand? <a className="underline" href="mailto:author@hayleyraejohnson.com">Contact Lady Liza</a>.</p>
    </div></main>
  </div>;
}

export default function Verify() {
  const location = useLocation();
  return <VerificationForm key={location.search} search={location.search} />;
}
